export interface FormatPhoneOptions {
  /**
   * Locale hint (`en-US`, `es-MX`, `fr-CA`). Reserved for future
   * region-specific grouping; the North American Numbering Plan layout is
   * shared across the three MVP locales (US / MX / CA all use `+1`), so today
   * the output is identical. Passing it keeps call sites future-proof.
   */
  locale?: string;
}

const NANP_RE = /^\+1(\d{3})(\d{3})(\d{4})$/;

/**
 * Formats a stored E.164 phone number for display.
 *
 * North-American numbers (`+1XXXXXXXXXX`) render as `(301) 555-0142` — the `+1`
 * country code is dropped for a cleaner display (all three MVP locales are `+1`).
 * Any other country code, or a value that isn't clean E.164, is returned
 * unchanged (best-effort) rather than mangled, so a `+44` or a partially-entered
 * number still displays legibly (and keeps its country code, which is meaningful
 * there).
 *
 * The stored/edited value stays E.164 (`UpdateUserRequest.contactPhoneNumber`);
 * this helper is display-only. Reuse it wherever a `contactPhoneNumber` is
 * surfaced so the pretty format is consistent (my profile, user detail, lines, …).
 */
export function formatPhone(
  value: string | null | undefined,
  _opts: FormatPhoneOptions = {},
): string {
  if (value == null) return '';
  const trimmed = value.trim();
  if (trimmed === '') return '';

  const match = NANP_RE.exec(trimmed);
  if (!match) return trimmed;

  const [, area, prefix, line] = match;
  return `(${area}) ${prefix}-${line}`;
}
